<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CompanyDataImportModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const form = ref(null);

  const $refs = {
    get form() {
      return form.value;
    },
  };

  const file = ref(null);
  const headered = ref(true);
  const colnum_number = ref(1);
  const colnum_name = ref(2);
  const colnum_email = ref(3);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    file: file.value,
    headered: headered.value,
    colnum_number: colnum_number.value,
    colnum_name: colnum_name.value,
    colnum_email: colnum_email.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'file' },
      { name: 'headered' },
      { name: 'colnum_number' },
      { name: 'colnum_name' },
      { name: 'colnum_email' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      file,
      headered,
      colnum_number,
      colnum_name,
      colnum_email,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Import client data') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_2pzggs97r_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_5534025912102772_register_3hek04pbi_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data
        ? $event.data.response.data.updated_records
        : 0,
    };
  };
  const act_5534025912102772_notification_c14r4au89_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.ngettext(
        'One record imported.',
        '${n} records imported.',
        $event.data.resultLength
      ),
    };
  };
  const act_5534025912102772_notification_c14r4au89_getWhen = async (
    $event
  ) => {
    return $event.data.resultLength > 0;
  };
  const act_5534025912102772_notification_jybf23mq3_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.gettext('Nothing has been imported.'),
    };
  };
  const act_5534025912102772_notification_jybf23mq3_getWhen = async (
    $event
  ) => {
    return !$event.data.resultLength;
  };
  const act_5534025912102772_closeModal_oocznp0rf_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_5534025912102772_reloadGrid_0eyss41wq_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_5534025912102772_request_dp5x3wb1m_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'import_clients',
      data: {
        header: headered.value,
        file: file.value,
        colnum_number: colnum_number.value,
        colnum_name: colnum_name.value,
        colnum_email: colnum_email.value,
      },
    };
  };
  const act_2434514588044777_closeModal_tt4yzn9d2_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_2pzggs97r =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_2pzggs97r_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ha8h7ld72 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_2pzggs97r,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ha8h7ld72],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_cjbwat7oi =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_nwlnwzj44 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_cjbwat7oi,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_nwlnwzj44],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_lqnsd85sm =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_ec353gc96 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_lqnsd85sm,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_ec353gc96],
    event: 'close',
  });
  const act_5534025912102772_register_3hek04pbi =
    new actionTypes.RegisterAction({
      actionArgs: act_5534025912102772_register_3hek04pbi_getActionArgs,
    });
  const inst_evh_5534025912102772_success_0b5sgev8p =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_register_3hek04pbi,
      event: 'success',
      displayName: 'register',
    });
  const act_5534025912102772_notification_c14r4au89 =
    new actionTypes.NotificationAction({
      actionArgs: act_5534025912102772_notification_c14r4au89_getActionArgs,
      when: act_5534025912102772_notification_c14r4au89_getWhen,
    });
  const inst_evh_5534025912102772_success_ouu1jd8e7 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_notification_c14r4au89,
      event: 'success',
      displayName: 'notification',
    });
  const act_5534025912102772_notification_jybf23mq3 =
    new actionTypes.NotificationAction({
      actionArgs: act_5534025912102772_notification_jybf23mq3_getActionArgs,
      when: act_5534025912102772_notification_jybf23mq3_getWhen,
    });
  const inst_evh_5534025912102772_success_iujj4eozu =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_notification_jybf23mq3,
      event: 'success',
      displayName: 'notification',
    });
  const act_5534025912102772_closeModal_oocznp0rf =
    new actionTypes.CloseModalAction({
      actionArgs: act_5534025912102772_closeModal_oocznp0rf_getActionArgs,
    });
  const inst_evh_5534025912102772_success_pqbcroy0j =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeModal_oocznp0rf,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_5534025912102772_reloadGrid_0eyss41wq =
    new actionTypes.ReloadGridAction({
      actionArgs: act_5534025912102772_reloadGrid_0eyss41wq_getActionArgs,
    });
  const inst_evh_5534025912102772_success_5t1ae3lyx =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadGrid_0eyss41wq,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_5534025912102772_request_dp5x3wb1m = new actionTypes.RequestAction({
    actionArgs: act_5534025912102772_request_dp5x3wb1m_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_5534025912102772_success_0b5sgev8p,
      inst_evh_5534025912102772_success_ouu1jd8e7,
      inst_evh_5534025912102772_success_iujj4eozu,
      inst_evh_5534025912102772_success_pqbcroy0j,
      inst_evh_5534025912102772_success_5t1ae3lyx,
    ],
  });
  const inst_evh_5534025912102772_submit_u40fd2nnd =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_request_dp5x3wb1m,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_5534025912102772_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_submit_u40fd2nnd],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_tt4yzn9d2 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_tt4yzn9d2_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_32djl75in =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_tt4yzn9d2,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_32djl75in],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ha8h7ld72,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_nwlnwzj44,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_ec353gc96,
    inst_evh_8564662037462133_close,
    inst_evh_5534025912102772_success_0b5sgev8p,
    inst_evh_5534025912102772_success_ouu1jd8e7,
    inst_evh_5534025912102772_success_iujj4eozu,
    inst_evh_5534025912102772_success_pqbcroy0j,
    inst_evh_5534025912102772_success_5t1ae3lyx,
    inst_evh_5534025912102772_submit_u40fd2nnd,
    submit: inst_evh_5534025912102772_submit_u40fd2nnd,
    inst_evh_5534025912102772_submit,
    inst_evh_2434514588044777_clickPreventStop_32djl75in,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_ha8h7ld72 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ha8h7ld72.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_nwlnwzj44 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_nwlnwzj44.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_ec353gc96 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_ec353gc96.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_0b5sgev8p = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_0b5sgev8p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_ouu1jd8e7 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_ouu1jd8e7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_iujj4eozu = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_iujj4eozu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_pqbcroy0j = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_pqbcroy0j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_5t1ae3lyx = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_5t1ae3lyx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit_u40fd2nnd = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit_u40fd2nnd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_32djl75in = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_32djl75in.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CompanyDataImportModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <div class="container">
        <ItpForm
          name="clientDataImport"
          ref="form"
          v-if="loaded"
          @submit="evh_5534025912102772_submit($event, {})"
        >
          <ItpFormGroup
            row
            name="file"
            :label="gettext('File')"
            labelFor="file"
            :label-cols-md="2"
            :content-cols-md="10"
          >
            <ItpFormFile
              v-model="file"
              name="file"
              size="md"
              accept=".xlsx, .csv"
              required
            >
            </ItpFormFile>
          </ItpFormGroup>
          <ItpFormGroup
            row
            name="header"
            :label-cols-md="0"
            :content-cols-md="12"
          >
            <ItpFormCheckbox
              name="headered"
              :label="gettext('Import file has header')"
              v-model="headered"
              size="md"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            row
            name="colnum_number"
            :label="gettext('Number column number')"
            :label-cols-md="5"
            :content-cols-md="3"
          >
            <ItpFormInput
              type="number"
              name="colnum_number"
              v-model="colnum_number"
              size="md"
              number
              required
              :min="1"
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            row
            name="colnum_name"
            :label="gettext('Name column number')"
            :label-cols-md="5"
            :content-cols-md="3"
          >
            <ItpFormInput
              type="number"
              name="colnum_name"
              v-model="colnum_name"
              size="md"
              number
              required
              :min="1"
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            row
            name="colnum_email"
            :label="gettext('Email column number')"
            :label-cols-md="5"
            :content-cols-md="3"
          >
            <ItpFormInput
              type="number"
              name="colnum_email"
              v-model="colnum_email"
              size="md"
              number
              required
              :min="1"
            >
            </ItpFormInput>
          </ItpFormGroup>
        </ItpForm>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.clientDataImport && forms.clientDataImport.id"
          :text="gettext('Import')"
          type="submit"
          variant="primary"
          icon="fa-file-import"
          :spinning="actions.submit.isRunning"
          :disabled="
            !forms.clientDataImport || !forms.clientDataImport.submittable
          "
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
